import * as THREE from 'three';

/*
  Placement maths for the backdrop, kept pure so it can be reasoned about and
  tested without a WebGL context.

  progress 0 -> garments on hangers drift scattered on an annulus (an unsorted
  marketplace).
  progress 1 -> every hanger hooks onto a turning garment rail. The cloth
  itself is simulated (see clothSim.ts), so it swings and ripples as the
  hangers are carried there.
*/

/*
  One source of truth for the six palette shades.

  `hex` is the shade as shown in the Palette section. `backdrop` is a
  higher-chroma sibling of the same hue: the 3D layer is blurred and sits below
  70% opacity, so the shown values wash out to grey there and need more chroma
  to survive.
*/
export const PALETTE = [
  { name: 'Blush', hex: '#DCAA96', backdrop: '#F0946B' },
  { name: 'Sage', hex: '#8BA88A', backdrop: '#6FB271' },
  { name: 'Mauve', hex: '#B8959E', backdrop: '#C97E97' },
  { name: 'Sand', hex: '#D4BFA0', backdrop: '#E3BC6B' },
  { name: 'Slate', hex: '#7F8EA8', backdrop: '#6C8CC4' },
  { name: 'Umber', hex: '#A0755A', backdrop: '#B26A3C' },
] as const;

/*
  The palette hues plus a few extra wardrobe colours, so the floating garments
  read as a varied rail of clothes rather than six repeating shades.
*/
export const BACKDROP_TONES = [
  ...PALETTE.map((shade) => shade.backdrop),
  '#E4566B', // cherry
  '#3FA7A0', // teal
  '#8A6FD1', // lavender
  '#F2C94C', // mustard
  '#2F4A7A', // navy
  '#F7F3EA', // ivory
];

export const RAIL_RADIUS = 11.4;
/** Height of each rail ring; garments hang below it. */
export const RAIL_HEIGHTS = [5.4, -0.9];
export const SCATTER_INNER = 9;
export const SCATTER_SPREAD = 8;

export interface GarmentSeed {
  scatter: THREE.Vector3;
  scatterYaw: number;
  theta: number;
  rail: number;
  scale: number;
  tone: string;
  kind: number;
  /** Phase offset for the idle float, so garments don't bob in unison. */
  phase: number;
  /** Seeds the fabric pattern. */
  seed: number;
}

/** Deterministic, so the composition is identical on every load. */
export const makeSeeds = (count: number, kinds: number): GarmentSeed[] => {
  let state = 1337;
  const rnd = () => (state = (state * 16807) % 2147483647) / 2147483647;
  const rails = RAIL_HEIGHTS.length;
  const perRail = Math.ceil(count / rails);

  return Array.from({ length: count }, (_, i) => {
    const rail = i % rails;
    const indexOnRail = Math.floor(i / rails);
    const angle = rnd() * Math.PI * 2;
    const dist = SCATTER_INNER + rnd() * SCATTER_SPREAD;

    return {
      scatter: new THREE.Vector3(
        Math.cos(angle) * dist,
        Math.sin(angle) * dist * 0.72,
        (rnd() - 0.5) * 12 - 2
      ),
      scatterYaw: (rnd() - 0.5) * Math.PI * 1.4,
      theta: (indexOnRail / perRail) * Math.PI * 2 + rail * (Math.PI / perRail),
      rail,
      scale: 1.55 + rnd() * 0.4,
      // Colour and kind step at co-prime rates, so no garment/colour pair repeats in lockstep.
      tone: BACKDROP_TONES[(i * 7) % BACKDROP_TONES.length],
      kind: i % kinds,
      phase: rnd() * Math.PI * 2,
      seed: i + 1,
    };
  });
};

/** Ease-out so hangers settle onto the rail rather than arriving linearly. */
export const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

const railPos = new THREE.Vector3();

/**
 * Place one hanger at eased progress `p` and rail rotation `spin`.
 * `hookTop` is the hook's height above the garment origin (garment units),
 * so the hook — not the garment centre — sits on the rail.
 */
export const placeHanger = (
  seed: GarmentSeed,
  hookTop: number,
  p: number,
  spin: number,
  time: number,
  out: THREE.Object3D
) => {
  const theta = seed.theta + spin;
  const drift = time * 0.6 + seed.phase;

  railPos.set(
    Math.cos(theta) * RAIL_RADIUS,
    RAIL_HEIGHTS[seed.rail] - hookTop * seed.scale,
    Math.sin(theta) * RAIL_RADIUS
  );
  out.position.lerpVectors(seed.scatter, railPos, p);
  // Idle float while scattered; on the rail the hook is fixed and only the cloth moves.
  out.position.y += Math.sin(drift) * 0.5 * (1 - p);

  out.rotation.set(0, THREE.MathUtils.lerp(seed.scatterYaw, -theta + Math.PI / 2, p), Math.sin(drift * 0.7) * 0.12 * (1 - p));
  out.scale.setScalar(seed.scale);
  out.updateMatrixWorld();
};
